import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { TrendingUp, Sparkles, Gift, Calendar, BarChart3, Download } from "lucide-react";

const MONTH_NAMES = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

export default function AdminFinances() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/finances").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const maxRevenue = useMemo(() => {
    if (!data?.by_month) return 1;
    return Math.max(1, ...data.by_month.map((m) => m.total));
  }, [data]);

  const formatMonth = (yyyymm) => {
    if (!yyyymm || yyyymm.length < 7) return yyyymm;
    const [y, m] = yyyymm.split("-");
    return `${MONTH_NAMES[parseInt(m, 10) - 1] || m} ${y}`;
  };

  const exportCSV = () => {
    if (!data?.by_month) return;
    const rows = [
      ["Monat", "Buchungen Anzahl", "Buchungen Umsatz CHF", "Gutscheine Anzahl", "Gutscheine Umsatz CHF", "Gesamt CHF"],
      ...data.by_month.map((m) => [
        formatMonth(m.month),
        m.bookings_count,
        Math.round(m.bookings_revenue),
        m.vouchers_count,
        Math.round(m.vouchers_revenue),
        Math.round(m.total),
      ]),
    ];
    const csv = rows.map((r) => r.join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ayurveda-finanzen-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="p-8 text-ink-muted">Lädt…</div>;
  if (!data) return <div className="p-8 text-ink-muted">Keine Daten verfügbar.</div>;

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-6xl" data-testid="admin-finances-page">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="font-display text-3xl sm:text-4xl text-ink flex items-center gap-3">
          <TrendingUp className="w-7 h-7 text-terracotta" strokeWidth={1.5} /> Finanzen
        </h1>
        <button onClick={exportCSV} data-testid="finances-export-csv"
          className="inline-flex items-center gap-2 px-4 py-2.5 border border-border rounded-full text-sm hover:bg-sand-soft">
          <Download className="w-4 h-4" strokeWidth={1.5} /> CSV Export
        </button>
      </div>

      {/* KPI cards */}
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Calendar} label={`${formatMonth(data.current_month)} (aktuell)`} value={`CHF ${Math.round(data.current_month_revenue)}`} color="terracotta" />
        <Stat icon={BarChart3} label={`Jahr ${data.current_year}`} value={`CHF ${Math.round(data.current_year_revenue)}`} color="sage" />
        <Stat icon={Sparkles} label={`Massagen (${data.bookings_count})`} value={`CHF ${Math.round(data.bookings_total)}`} />
        <Stat icon={Gift} label={`Gutscheine (${data.vouchers_count})`} value={`CHF ${Math.round(data.vouchers_total)}`} />
      </div>

      <div className="mt-6 p-5 bg-ink text-sand rounded-lg flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-sand/60">Gesamtumsatz aller Zeiten</div>
          <div className="font-display text-4xl sm:text-5xl mt-1">CHF {Math.round(data.total_revenue).toLocaleString("de-CH")}</div>
        </div>
        <div className="text-xs text-sand/60">Buchungen + bezahlte Gutscheine</div>
      </div>

      {/* Monthly breakdown */}
      <div className="mt-10">
        <h2 className="font-display text-2xl text-ink mb-4">Umsatz nach Monat</h2>
        {data.by_month.length === 0 ? (
          <div className="bg-white border border-dashed border-border rounded-md p-10 text-center text-ink-muted">
            Noch keine Umsätze erfasst.
          </div>
        ) : (
          <div className="bg-white border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="finances-by-month-table">
              <thead className="bg-muted/60 text-xs uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="text-left px-4 py-3">Monat</th>
                  <th className="text-right px-4 py-3">Massagen</th>
                  <th className="text-right px-4 py-3">Gutscheine</th>
                  <th className="text-right px-4 py-3">Gesamt</th>
                  <th className="text-left px-4 py-3 w-1/3">Verteilung</th>
                </tr>
              </thead>
              <tbody>
                {data.by_month.map((m) => (
                  <tr key={m.month} className="border-t border-border">
                    <td className="px-4 py-3 text-ink font-medium">{formatMonth(m.month)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-ink">CHF {Math.round(m.bookings_revenue)}</div>
                      <div className="text-xs text-ink-muted">{m.bookings_count}× Termine</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-ink">CHF {Math.round(m.vouchers_revenue)}</div>
                      <div className="text-xs text-ink-muted">{m.vouchers_count}× Gutscheine</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-ink font-display text-lg">CHF {Math.round(m.total)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-2 rounded-full bg-sand-soft overflow-hidden flex">
                        <div className="bg-terracotta" style={{ width: `${(m.bookings_revenue / maxRevenue) * 100}%` }} />
                        <div className="bg-sage" style={{ width: `${(m.vouchers_revenue / maxRevenue) * 100}%` }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3 flex items-center gap-4 text-xs text-ink-muted">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-terracotta" /> Massagen</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-sage" /> Gutscheine</span>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white border border-border rounded-md p-5">
    <div className="flex items-center gap-2">
      <Icon className={`w-4 h-4 ${color === "terracotta" ? "text-terracotta" : color === "sage" ? "text-sage" : "text-ink-muted"}`} strokeWidth={1.5} />
      <div className="text-xs uppercase tracking-widest text-ink-muted">{label}</div>
    </div>
    <div className="mt-2 font-display text-2xl sm:text-3xl text-ink">{value}</div>
  </div>
);
